// Fluides frigorigènes (31/10/2026, migration 050) : intervenant de la fiche fluides d'un bon, attestations, sous-traitants et
// BILAN ANNUEL pour la déclaration de fin d'année.
//  - Fiche du bon : le technicien de la société est NOMMÉ (choisi parmi les comptes, jamais en texte libre) ; son attestation
//    d'aptitude et l'attestation de capacité de la société sont reprises par le serveur ; ou un sous-traitant enregistré, qui
//    intervient au nom de la société et porte la fiche et la déclaration. Ce qui manque pour signer est dit en clair.
//  - Page « Fluides frigorigènes » (encadrement) : bilan annuel (écran, CSV, PDF), registre des sous-traitants, attestations
//    (modifiées par l'administrateur). Le technicien seul n'y a pas accès (la page répond « introuvable »).
// Pensé pour le téléphone : cartes plutôt que tableaux larges, cibles de 44 px, aucune barre de défilement horizontal.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, Pencil, Plus, ShieldCheck } from 'lucide-react';
import { LIBELLES_INTERVENANT, PHRASE_SOUS_TRAITANT, type AttestationFluides, type IntervenantFluides } from '@shared/bons-intervention.ts';
import { api, messageErreur } from '../../lib/api.ts';
import { date } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useModulesEtat } from '../../lib/modules.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, Onglets, Panneau, useNotifier,
} from '../../components/ui.tsx';
import { OngletsInterventions } from '../interventions/chantiers/commun.tsx';
import { PageIntrouvable } from '../legal/PageIntrouvable.tsx';

// ─── Types servis par l'API ───
export interface SousTraitant { id: string; raison_sociale: string; siret: string | null; adresse: string | null; capacite: AttestationFluides; actif: boolean; demo: boolean }
export interface Intervenants {
  capacite: (AttestationFluides & { titulaire: string }) | null;
  techniciens: { id: string; nom: string; attestation_enregistree: boolean; attestation: (AttestationFluides & { categorie: string }) | null }[];
  sous_traitants: SousTraitant[];
  droits: { gerer: boolean; aptitudes: boolean; capacite: boolean };
}
interface LigneBilan { fluide: string; bons: number; charge_kg: number; dont_vierge_kg: number; dont_recycle_kg: number; dont_regenere_kg: number; recupere_kg: number; reintroduit_kg: number; remis_kg: number }
interface BonBilan { id: string; reference: string; version_no: number; date: string; site: string; fluide: string; intervenant: string | null; charge_kg: number; recupere_kg: number }
interface Bilan {
  annee: number; societe: string; capacite: AttestationFluides | null; perimetre_complet: boolean;
  societe_lignes: LigneBilan[]; societe_total: LigneBilan; societe_bons: BonBilan[];
  sous_traitants: { cle: string; raison_sociale: string; siret: string | null; attestation: string | null; lignes: LigneBilan[]; total: LigneBilan; bons: BonBilan[] }[];
  avertissements: string[]; colonnes: Record<string, string>;
}

const kg = (v: number) => `${v.toLocaleString('fr-FR', { maximumFractionDigits: 3 })} kg`;
const texteAttestation = (a: Partial<AttestationFluides> & { categorie?: string | null } | null | undefined) =>
  (a?.numero ? [a.categorie ? `catégorie ${a.categorie}` : null, `n° ${a.numero}`, a.organisme, a.fin_validite ? `valable jusqu’au ${date(a.fin_validite)}` : null].filter(Boolean).join(' — ') : null);

// ═══ 1. Intervenant fluides dans la fiche d'un bon ═══════════════════════════════════════════════════════════════════
export function ChampIntervenantFluides({ valeur, changer, ecrire, manque, avertissements }: {
  valeur: IntervenantFluides | null | undefined; changer: (v: IntervenantFluides) => void; ecrire: boolean;
  manque: { message: string }[]; avertissements: string[];
}) {
  const q = useQuery({ queryKey: ['bons-fluides-intervenants'], queryFn: () => api.get<Intervenants>('/api/bons/fluides/intervenants'), enabled: ecrire, staleTime: 60_000 });
  const d = q.data;
  const type = valeur?.type ?? null;
  const technicien = valeur?.type === 'interne' && valeur.user_id ? d?.techniciens.find((x) => x.id === valeur.user_id) : undefined;
  const st = valeur?.type === 'sous_traitant' && valeur.sous_traitant_id ? d?.sous_traitants.find((x) => x.id === valeur.sous_traitant_id) : undefined;
  // Ce qui est affiché : la copie faite par le serveur dans le bon (après enregistrement), sinon la liste du moment (aperçu).
  const aptitude = valeur?.type === 'interne' ? (valeur.aptitude ?? technicien?.attestation ?? null) : null;
  const capaciteSociete = valeur?.type === 'interne' ? (valeur.capacite ?? d?.capacite ?? null) : null;
  const capaciteST = valeur?.type === 'sous_traitant' ? (valeur.capacite ?? st?.capacite ?? null) : null;
  return (
    <div className="pile bi-intervenant">
      <div className="segment bi-segment-intervenant" role="group" aria-label="Intervenant fluides">
        <button type="button" aria-pressed={type === 'interne'} onClick={() => changer({ type: 'interne', user_id: null })}>{LIBELLES_INTERVENANT.interne}</button>
        <button type="button" aria-pressed={type === 'sous_traitant'} onClick={() => changer({ type: 'sous_traitant', sous_traitant_id: null, technicien_nom: null, technicien_attestation: null })}>{LIBELLES_INTERVENANT.sous_traitant}</button>
      </div>
      {!type && <p className="petit discret">Choisissez qui a manipulé les fluides : il est nommé sur la fiche, pour la conservation du bon et la déclaration de fin d’année.</p>}
      {valeur?.type === 'interne' && (
        <div className="bi-grille">
          {ecrire ? (
            <ChampChoix libelle="Technicien (nommé)" value={valeur.user_id ?? ''} vide="Choisir le technicien…"
              options={(d?.techniciens ?? []).map((x) => ({ valeur: x.id, libelle: `${x.nom}${x.attestation_enregistree ? '' : ' — attestation non enregistrée'}` }))}
              onChange={(e) => changer({ type: 'interne', user_id: e.target.value || null })} />
          ) : <p className="petit"><strong>Technicien :</strong> {valeur.nom ?? 'non nommé'}</p>}
          <p className="petit"><strong>Attestation d’aptitude :</strong> {texteAttestation(aptitude) ?? (technicien?.attestation_enregistree ? 'enregistrée (numéro visible de l’encadrement et de l’intéressé)' : 'non enregistrée')}</p>
          <p className="petit"><strong>Attestation de capacité de la société :</strong> {texteAttestation(capaciteSociete) ?? 'non renseignée'}</p>
        </div>
      )}
      {valeur?.type === 'sous_traitant' && (
        <>
          <p className="petit bi-phrase-st">{PHRASE_SOUS_TRAITANT}</p>
          <div className="bi-grille">
            {ecrire ? (
              <ChampChoix libelle="Sous-traitant" value={valeur.sous_traitant_id ?? ''} vide="Choisir le sous-traitant…"
                options={(d?.sous_traitants ?? []).map((x) => ({ valeur: x.id, libelle: x.raison_sociale }))}
                onChange={(e) => changer({ ...valeur, sous_traitant_id: e.target.value || null })} />
            ) : <p className="petit"><strong>Sous-traitant :</strong> {valeur.raison_sociale ?? 'non choisi'}</p>}
            <p className="petit"><strong>Son attestation de capacité :</strong> {texteAttestation(capaciteST) ?? 'non renseignée'}</p>
            <Champ libelle="Technicien du sous-traitant — nom" value={valeur.technicien_nom ?? ''} maxLength={200}
              onChange={(e) => changer({ ...valeur, technicien_nom: e.target.value || null })} />
            <Champ libelle="N° d’attestation d’aptitude de ce technicien" mono value={valeur.technicien_attestation ?? ''} maxLength={80}
              onChange={(e) => changer({ ...valeur, technicien_attestation: e.target.value || null })} />
          </div>
          {ecrire && d && !d.sous_traitants.length && <p className="petit discret">Aucun sous-traitant enregistré.{d.droits.gerer ? ' ' : ' Demandez à votre référent ou chargé d’affaires de l’enregistrer.'}
            {d.droits.gerer && <Link to="/interventions/bons/fluides?onglet=sous-traitants">Enregistrer un sous-traitant</Link>}</p>}
        </>
      )}
      {manque.length > 0 && (
        <Alerte ton="ambre" titre="Pour faire signer ce bon, il manque :">
          <ul className="bi-manques">{manque.map((m) => <li key={m.message}>{m.message}</li>)}</ul>
        </Alerte>
      )}
      {avertissements.map((a) => <Alerte key={a} ton="ambre" titre="Avertissement">{a} La signature reste possible ; l’avertissement figure sur le PDF.</Alerte>)}
    </div>
  );
}

// ═══ 2. Page « Fluides frigorigènes » (encadrement) ═════════════════════════════════════════════════════════════════
type Onglet = 'bilan' | 'sous-traitants' | 'attestations';
export function PageFluides() {
  const { charge, modules } = useModulesEtat();
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  if (!charge) return <Chargement />;
  if (!modules.bons_intervention || !peut('bons.fluides')) return <PageIntrouvable />;
  const demande = params.get('onglet') as Onglet | null;
  const onglet: Onglet = demande === 'sous-traitants' || demande === 'attestations' ? demande : 'bilan';
  const changer = (o: Onglet) => { const p = new URLSearchParams(params); if (o === 'bilan') p.delete('onglet'); else p.set('onglet', o); setParams(p, { replace: true }); };
  return (
    <div className="pile-l bi-fluides">
      <EntetePage fil={<nav className="petit"><Link to="/interventions/bons">Bons d’intervention</Link></nav>} titre="Fluides frigorigènes"
        sousTitre="Bilan annuel pour la déclaration de fin d’année, sous-traitants et attestations." />
      <OngletsInterventions />
      <Onglets libelle="Fluides frigorigènes" actif={onglet} changer={changer}
        onglets={[{ id: 'bilan', libelle: 'Bilan annuel' }, { id: 'sous-traitants', libelle: 'Sous-traitants' }, { id: 'attestations', libelle: 'Attestations' }]} />
      {onglet === 'bilan' && <OngletBilan />}
      {onglet === 'sous-traitants' && <OngletSousTraitants />}
      {onglet === 'attestations' && <OngletAttestations />}
    </div>
  );
}

function CarteLigne({ l, colonnes, total }: { l: LigneBilan; colonnes: Record<string, string>; total?: boolean }) {
  return (
    <div className={`bi-bilan-carte${total ? ' bi-bilan-total' : ''}`}>
      <div className="rangee entre"><strong>{l.fluide}</strong><span className="petit discret">{l.bons} bon{l.bons > 1 ? 's' : ''}</span></div>
      <dl className="bi-bilan-dl">
        {Object.entries(colonnes).map(([k, lib]) => <div key={k}><dt>{lib}</dt><dd className="chiffre">{kg((l as any)[k] ?? 0)}</dd></div>)}
      </dl>
    </div>
  );
}

function ListeBonsBilan({ bons }: { bons: BonBilan[] }) {
  if (!bons.length) return null;
  return (
    <details className="bi-bilan-bons petit">
      <summary>Voir les {bons.length} bon{bons.length > 1 ? 's' : ''}</summary>
      <ul>{bons.map((b) => <li key={b.id}><Link to={`/interventions/bons/${b.id}`}>{b.reference}{b.version_no > 1 ? ` v${b.version_no}` : ''}</Link> — {date(b.date)} — {b.site} — {b.fluide}
        {b.intervenant ? ` — ${b.intervenant}` : ''} — chargé {kg(b.charge_kg)}, récupéré {kg(b.recupere_kg)}</li>)}</ul>
    </details>
  );
}

function OngletBilan() {
  const [params, setParams] = useSearchParams();
  const actuelle = new Date().getFullYear();
  const annee = Number(params.get('annee')) || actuelle;
  const q = useQuery({ queryKey: ['bons-fluides-bilan', annee], queryFn: () => api.get<Bilan>(`/api/bons/fluides/bilan?annee=${annee}`) });
  const choisir = (v: string) => { const p = new URLSearchParams(params); p.set('annee', v); setParams(p, { replace: true }); };
  const b = q.data;
  return (
    <div className="pile-l">
      <Panneau>
        <div className="bi-bilan-entete">
          <ChampChoix libelle="Année civile" value={String(annee)} onChange={(e) => choisir(e.target.value)}
            options={Array.from({ length: 6 }, (_, i) => String(actuelle - i)).map((x) => ({ valeur: x, libelle: x }))} />
          <span className="groupe-btn">
            <a className="btn" href={`/api/bons/fluides/bilan.csv?annee=${annee}`} download><FileDown aria-hidden />CSV</a>
            <a className="btn btn-principal" href={`/api/bons/fluides/bilan.pdf?annee=${annee}`} download><FileDown aria-hidden />PDF du bilan</a>
          </span>
        </div>
        <p className="petit secondaire">Quantités tirées des bons d’intervention <strong>signés</strong> portant une fiche fluides (pour une référence, sa dernière version signée).
          À vérifier avant la déclaration annuelle à l’organisme qui a délivré l’attestation de capacité de la société.</p>
      </Panneau>
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {b && (
        <>
          {b.avertissements.length > 0 && <Alerte ton="ambre" titre="À vérifier"><ul className="bi-manques">{b.avertissements.map((a) => <li key={a}>{a}</li>)}</ul></Alerte>}
          <Panneau titre={`Interventions de la société — total déclaré par la société (${b.annee})`}>
            <p className="petit secondaire">Attestation de capacité : {texteAttestation(b.capacite) ?? 'non renseignée'}</p>
            {b.societe_lignes.length === 0 ? <EtatVide titre="Aucune intervention" texte={`Aucun bon signé avec fiche fluides en ${b.annee}.`} /> : (
              <div className="bi-bilan-grille">
                {b.societe_lignes.map((l) => <CarteLigne key={l.fluide} l={l} colonnes={b.colonnes} />)}
                <CarteLigne l={b.societe_total} colonnes={b.colonnes} total />
              </div>
            )}
            <ListeBonsBilan bons={b.societe_bons} />
          </Panneau>
          <Panneau titre="Interventions des sous-traitants — exclues du total de la société">
            <p className="petit secondaire">Déclaration annuelle de ces quantités : à voir avec le sous-traitant.</p>
            {b.sous_traitants.length === 0 ? <p className="petit discret">Aucune intervention de sous-traitant en {b.annee}.</p> : b.sous_traitants.map((s) => (
              <section key={s.cle} className="pile bi-bilan-st">
                <h3 className="bi-sous-titre">{s.raison_sociale}{s.siret ? ` — SIRET ${s.siret}` : ''}{s.attestation ? ` — attestation de capacité ${s.attestation}` : ''}</h3>
                <div className="bi-bilan-grille">{s.lignes.map((l) => <CarteLigne key={l.fluide} l={l} colonnes={b.colonnes} />)}</div>
                <ListeBonsBilan bons={s.bons} />
              </section>
            ))}
          </Panneau>
        </>
      )}
    </div>
  );
}

// ─── Sous-traitants ───
function OngletSousTraitants() {
  const q = useQuery({ queryKey: ['bons-fluides-st'], queryFn: () => api.get<{ sous_traitants: SousTraitant[]; aujourdhui: string }>('/api/bons/fluides/sous-traitants') });
  const [edite, setEdite] = useState<SousTraitant | 'nouveau' | null>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const l = q.data!.sous_traitants;
  return (
    <Panneau titre="Sous-traitants fluides" actions={<Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setEdite('nouveau')}>Nouveau sous-traitant</Bouton>}>
      <p className="petit secondaire">Enregistrés une fois, proposés sur chaque fiche fluides. {PHRASE_SOUS_TRAITANT}</p>
      {!l.length && <EtatVide titre="Aucun sous-traitant" texte="Enregistrez la raison sociale, le SIRET et l’attestation de capacité de vos sous-traitants frigoristes." />}
      <ul className="liste">
        {l.map((s) => (
          <li key={s.id} className="liste-element bi-st">
            <div className="liste-element-corps">
              <strong>{s.raison_sociale}</strong>
              <span className="petit">{[s.siret && `SIRET ${s.siret}`, s.adresse].filter(Boolean).join(' — ') || 'SIRET et adresse non renseignés'}</span>
              <span className="petit">Attestation de capacité : {texteAttestation(s.capacite) ?? <span className="inconnu">non renseignée</span>}</span>
              <span className="rangee">
                {!s.actif && <Badge>Désactivé</Badge>}
                {s.demo && <Badge ton="ambre">Démo</Badge>}
                {s.capacite.fin_validite && s.capacite.fin_validite < q.data!.aujourdhui && <Badge ton="danger">Attestation périmée</Badge>}
              </span>
            </div>
            <Bouton icone={<Pencil aria-hidden />} onClick={() => setEdite(s)}>Modifier</Bouton>
          </li>
        ))}
      </ul>
      {edite && <DialogueSousTraitant st={edite === 'nouveau' ? null : edite} fermer={() => setEdite(null)} />}
    </Panneau>
  );
}

function DialogueSousTraitant({ st, fermer }: { st: SousTraitant | null; fermer: () => void }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [f, setF] = useState({ raisonSociale: st?.raison_sociale ?? '', siret: st?.siret ?? '', adresse: st?.adresse ?? '', capaciteNumero: st?.capacite.numero ?? '',
    capaciteOrganisme: st?.capacite.organisme ?? '', capaciteFinValidite: st?.capacite.fin_validite ?? '', actif: st?.actif ?? true });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const enregistrer = useMutation({
    mutationFn: () => {
      const corps = { ...f, siret: f.siret || null, adresse: f.adresse || null, capaciteNumero: f.capaciteNumero || null, capaciteOrganisme: f.capaciteOrganisme || null,
        capaciteFinValidite: f.capaciteFinValidite || null };
      return st ? api.patch(`/api/bons/fluides/sous-traitants/${st.id}`, corps) : api.post('/api/bons/fluides/sous-traitants', corps);
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['bons-fluides-st'] }); await qc.invalidateQueries({ queryKey: ['bons-fluides-intervenants'] }); notifier('Sous-traitant enregistré.'); fermer(); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre={st ? `Modifier ${st.raison_sociale}` : 'Nouveau sous-traitant'} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={enregistrer.isPending} disabled={!f.raisonSociale.trim()} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton></>}>
      <div className="bi-grille">
        <Champ libelle="Raison sociale" value={f.raisonSociale} onChange={(e) => set({ raisonSociale: e.target.value })} maxLength={200} />
        <Champ libelle="SIRET (14 chiffres)" mono inputMode="numeric" value={f.siret} onChange={(e) => set({ siret: e.target.value })} maxLength={20} />
        <Champ libelle="Adresse" value={f.adresse} onChange={(e) => set({ adresse: e.target.value })} maxLength={300} />
        <Champ libelle="N° d’attestation de capacité" mono value={f.capaciteNumero} onChange={(e) => set({ capaciteNumero: e.target.value })} maxLength={80} />
        <Champ libelle="Organisme qui l’a délivrée" value={f.capaciteOrganisme} onChange={(e) => set({ capaciteOrganisme: e.target.value })} maxLength={200} />
        <Champ libelle="Fin de validité" type="date" value={f.capaciteFinValidite} onChange={(e) => set({ capaciteFinValidite: e.target.value })} />
      </div>
      {st && <label className="case"><input type="checkbox" checked={f.actif} onChange={(e) => set({ actif: e.target.checked })} /> Proposé sur les fiches fluides (actif)</label>}
    </Dialogue>
  );
}

// ─── Attestations (société et techniciens) ───
interface Attestations {
  capacite: AttestationFluides | null; titulaire: string;
  techniciens: { id: string; nom: string; attestation: (AttestationFluides & { categorie: string }) | null }[];
  droits: { aptitudes: boolean; capacite: boolean }; aujourdhui: string;
}
function OngletAttestations() {
  const q = useQuery({ queryKey: ['bons-fluides-attestations'], queryFn: () => api.get<Attestations>('/api/bons/fluides/attestations') });
  const [aptitude, setAptitude] = useState<Attestations['techniciens'][number] | null>(null);
  const [capacite, setCapacite] = useState(false);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const a = q.data!;
  const perimee = (x: AttestationFluides | null) => !!x?.fin_validite && x.fin_validite < a.aujourdhui;
  return (
    <div className="pile-l">
      <Panneau titre="Attestation de capacité de la société" actions={a.droits.capacite ? <Bouton icone={<ShieldCheck aria-hidden />} onClick={() => setCapacite(true)}>Modifier</Bouton> : undefined}>
        <p className="petit">{a.titulaire} — {texteAttestation(a.capacite) ?? <span className="inconnu">non renseignée</span>} {perimee(a.capacite) && <Badge ton="danger">Périmée</Badge>}</p>
        <p className="petit discret">Reprise automatiquement sur les fiches fluides des techniciens de la société.{a.droits.capacite ? '' : ' Modifiée par l’administrateur.'}</p>
      </Panneau>
      <Panneau titre="Attestations d’aptitude des techniciens">
        <p className="petit discret">Tenues par l’administrateur ; numéros visibles de l’encadrement et de l’intéressé. Reprises automatiquement sur la fiche du technicien nommé.</p>
        <ul className="liste">
          {a.techniciens.map((t) => (
            <li key={t.id} className="liste-element bi-st">
              <div className="liste-element-corps">
                <strong>{t.nom}</strong>
                <span className="petit">{texteAttestation(t.attestation) ?? <span className="inconnu">non enregistrée</span>} {perimee(t.attestation) && <Badge ton="danger">Périmée</Badge>}</span>
              </div>
              {a.droits.aptitudes && <Bouton icone={<Pencil aria-hidden />} onClick={() => setAptitude(t)}>{t.attestation ? 'Modifier' : 'Renseigner'}</Bouton>}
            </li>
          ))}
        </ul>
      </Panneau>
      {aptitude && <DialogueAptitude technicien={aptitude} fermer={() => setAptitude(null)} />}
      {capacite && <DialogueCapacite actuelle={a.capacite} fermer={() => setCapacite(false)} />}
    </div>
  );
}

/** Attestation d'aptitude d'un technicien (administrateur) ; réutilisé par l'onglet Comptes de l'administration. */
export function DialogueAptitude({ technicien, fermer }: { technicien: { id: string; nom: string; attestation: (AttestationFluides & { categorie?: string | null }) | null }; fermer: () => void }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const x = technicien.attestation;
  const [f, setF] = useState({ categorie: x?.categorie ?? '', numero: x?.numero ?? '', organisme: x?.organisme ?? '', finValidite: x?.fin_validite ?? '' });
  const set = (p: Partial<typeof f>) => setF((v) => ({ ...v, ...p }));
  const fin = async () => { for (const k of ['bons-fluides-attestations', 'bons-fluides-intervenants', 'admin-aptitudes']) await qc.invalidateQueries({ queryKey: [k] }); fermer(); };
  const enregistrer = useMutation({
    mutationFn: () => api.put(`/api/bons/fluides/aptitudes/${technicien.id}`, { categorie: f.categorie, numero: f.numero, organisme: f.organisme || null, finValidite: f.finValidite || null }),
    onSuccess: async () => { notifier('Attestation d’aptitude enregistrée.'); await fin(); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  const retirer = useMutation({
    mutationFn: () => api.del(`/api/bons/fluides/aptitudes/${technicien.id}`),
    onSuccess: async () => { notifier('Attestation retirée.'); await fin(); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre={`Attestation d’aptitude — ${technicien.nom}`} ouvert fermer={fermer}
      pied={<>{x && <Bouton variante="fantome" chargement={retirer.isPending} onClick={() => retirer.mutate()}>Retirer</Bouton>}<Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" chargement={enregistrer.isPending} disabled={!f.categorie.trim() || !f.numero.trim()} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton></>}>
      <div className="bi-grille">
        <Champ libelle="Catégorie" value={f.categorie} onChange={(e) => set({ categorie: e.target.value })} maxLength={40} aide="Ex. I, II… (arrêté de 2008) ou A1, A2… (arrêté du 21/11/2025)." />
        <Champ libelle="Numéro de l’attestation" mono value={f.numero} onChange={(e) => set({ numero: e.target.value })} maxLength={80} />
        <Champ libelle="Organisme évaluateur" value={f.organisme} onChange={(e) => set({ organisme: e.target.value })} maxLength={200} />
        <Champ libelle="Fin de validité" type="date" value={f.finValidite} onChange={(e) => set({ finValidite: e.target.value })} aide="Facultatif. Périmée à la date d’une intervention : avertissement sur le bon." />
      </div>
    </Dialogue>
  );
}

/** Attestation de capacité de la société (administrateur) ; réutilisé par l'onglet Entreprise de l'administration. */
export function DialogueCapacite({ actuelle, fermer }: { actuelle: AttestationFluides | null; fermer: () => void }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [f, setF] = useState({ numero: actuelle?.numero ?? '', organisme: actuelle?.organisme ?? '', finValidite: actuelle?.fin_validite ?? '' });
  const set = (p: Partial<typeof f>) => setF((v) => ({ ...v, ...p }));
  const enregistrer = useMutation({
    mutationFn: () => api.put('/api/bons/fluides/capacite', { numero: f.numero, organisme: f.organisme || null, finValidite: f.finValidite || null }),
    onSuccess: async () => {
      for (const k of ['bons-fluides-attestations', 'bons-fluides-intervenants']) await qc.invalidateQueries({ queryKey: [k] });
      notifier('Attestation de capacité enregistrée.'); fermer();
    },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre="Attestation de capacité de la société" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={enregistrer.isPending} disabled={!f.numero.trim()} onClick={() => enregistrer.mutate()}>Enregistrer</Bouton></>}>
      <div className="bi-grille">
        <Champ libelle="Numéro de l’attestation de capacité" mono value={f.numero} onChange={(e) => set({ numero: e.target.value })} maxLength={80} />
        <Champ libelle="Organisme agréé qui l’a délivrée" value={f.organisme} onChange={(e) => set({ organisme: e.target.value })} maxLength={200} />
        <Champ libelle="Fin de validité" type="date" value={f.finValidite} onChange={(e) => set({ finValidite: e.target.value })} />
      </div>
    </Dialogue>
  );
}

// ═══ 3. Réglages de l'administration (option « Bons d'intervention » active) ════════════════════════════════════════
/** Onglet Entreprise : attestation de capacité de la société (fluides frigorigènes). */
export function PanneauCapaciteFluides() {
  const { modules } = useModulesEtat();
  const { peut } = useSession();
  const actif = !!modules.bons_intervention && peut('bons.fluides');
  const q = useQuery({ queryKey: ['bons-fluides-attestations'], queryFn: () => api.get<Attestations>('/api/bons/fluides/attestations'), enabled: actif });
  const [ouvert, setOuvert] = useState(false);
  if (!actif || !q.data) return null;
  return (
    <Panneau titre="Attestation de capacité (fluides frigorigènes)" actions={q.data.droits.capacite ? <Bouton icone={<ShieldCheck aria-hidden />} onClick={() => setOuvert(true)}>Modifier</Bouton> : undefined}>
      <p className="petit">{texteAttestation(q.data.capacite) ?? <span className="inconnu">non renseignée</span>}</p>
      <p className="petit discret">Reprise automatiquement sur la fiche fluides des bons d’intervention faits par vos techniciens.</p>
      {ouvert && <DialogueCapacite actuelle={q.data.capacite} fermer={() => setOuvert(false)} />}
    </Panneau>
  );
}

/** Onglet Comptes : bouton « Attestation fluides » d'un compte (administrateur, option active). */
export function BoutonAptitudeCompte({ compte }: { compte: { id: string; display_name: string; active: boolean } }) {
  const { modules } = useModulesEtat();
  const { peut } = useSession();
  const actif = !!modules.bons_intervention && peut('admin.utilisateurs') && peut('bons.fluides');
  const q = useQuery({ queryKey: ['admin-aptitudes'], queryFn: () => api.get<Attestations>('/api/bons/fluides/attestations'), enabled: actif, staleTime: 30_000 });
  const [ouvert, setOuvert] = useState(false);
  if (!actif || !compte.active || !q.data) return null;
  const t = q.data.techniciens.find((x) => x.id === compte.id);
  if (!t) return null;
  return (
    <>
      <Bouton compact variante="fantome" icone={<ShieldCheck aria-hidden />} onClick={() => setOuvert(true)}>{t.attestation ? 'Attestation fluides' : 'Attestation fluides (à renseigner)'}</Bouton>
      {ouvert && <DialogueAptitude technicien={t} fermer={() => setOuvert(false)} />}
    </>
  );
}
